import type {
  ContentfulHeadlineBlock,
  ContentfulMediaImage,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import {
  CLOUDINARY_TRANSFORMATION_D2C_PORTRAIT,
  CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
  extractCloudinaryAssetUrl,
  getBlockId,
  getCloudinarySrcSet,
  resolveTransform,
} from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionGalleryPageBlock } from "@/types/page-block";

// Maps each rendered variant to its Cloudinary transform + display aspect ratio.
// The aspect ratio on the item is used by the UI component's aspect-ratio box;
// the transform determines the Cloudinary crop — they must stay in sync.
const GALLERY_VARIANT_CONFIG = {
  row: {
    transform: CLOUDINARY_TRANSFORMATION_D2C_PORTRAIT,
    aspectRatio: "3:4" as const,
  },
  staggered: {
    transform: CLOUDINARY_TRANSFORMATION_D2C_PORTRAIT,
    aspectRatio: "3:4" as const,
  },
  "simple-columns": {
    transform: CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
    aspectRatio: "1:1" as const,
  },
} as const;

type SectionGalleryVariant = "carousel" | "collage" | "narrow";

interface RichTextNode {
  value?: unknown;
  content?: unknown;
}

interface SectionGalleryTextInput {
  __typename: "SectionText";
  sys?: { id: string };
  headline?: ContentfulHeadlineBlock | null;
  text?: ContentfulTextBlock | null;
}

interface SectionGalleryItemInput {
  __typename: "GalleryItem" | "GalleryItemWithText";
  sys?: { id: string };
  internalName?: string | null;
  image?: ContentfulMediaImage | null;
  text?: SectionGalleryTextInput | null;
}

interface GalleryItem {
  id?: string;
  sys?: { id: string };
  textEntrySys?: { id: string };
  image: ContentfulMediaImage;
  internalName?: string | null;
  headline?: ContentfulHeadlineBlock;
  text?: ContentfulTextBlock;
}

export interface SectionGalleryInput {
  sys?: { id: string };
  __typename: "SectionGallery";
  internalName: string;
  variant: SectionGalleryVariant;
  fullWidth?: boolean | null;
  headline?: ContentfulHeadlineBlock;
  itemsCollection: {
    items: Array<SectionGalleryItemInput | null>;
  };
  uiConfiguration?: UiConfigurationInput;
}

function mapGalleryVariant(
  variant: SectionGalleryVariant
): SectionGalleryPageBlock["variant"] {
  switch (variant) {
    case "carousel":
      return "row";
    case "collage":
      return "staggered";
    case "narrow":
      return "simple-columns";
    default:
      return "row";
  }
}

function getGallerySectionId(item: SectionGalleryInput): string {
  return item.sys?.id ?? item.internalName;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasNonEmptyRichTextNode(node: unknown): boolean {
  if (!isObject(node)) {
    return false;
  }

  const richTextNode = node as RichTextNode;

  if (
    typeof richTextNode.value === "string" &&
    richTextNode.value.trim().length > 0
  ) {
    return true;
  }

  if (!Array.isArray(richTextNode.content)) {
    return false;
  }

  return richTextNode.content.some(hasNonEmptyRichTextNode);
}

function hasLinkedText(
  value: SectionGalleryTextInput | null | undefined
): value is SectionGalleryTextInput & { text: ContentfulTextBlock } {
  return hasNonEmptyRichTextNode(value?.text?.text?.json);
}

function normalizeGalleryItems(
  items: Array<SectionGalleryItemInput | null>,
  sectionId: string
): GalleryItem[] {
  return items
    .filter((item): item is SectionGalleryItemInput => item != null)
    .map((item) => {
      if (!item.image) {
        throw new Error(
          `Gallery Section "${sectionId}" contains an item without an image.`
        );
      }

      if (
        item.__typename === "GalleryItemWithText" &&
        !hasLinkedText(item.text)
      ) {
        throw new Error(
          `Gallery Section "${sectionId}" uses imagesWithText but contains an item without text.`
        );
      }

      return {
        id: item.sys?.id,
        sys: item.sys,
        textEntrySys: item.text?.sys,
        image: item.image,
        internalName: item.internalName,
        headline: item.text?.headline ?? undefined,
        text: item.text?.text ?? undefined,
      };
    });
}

function mapGalleryItems(
  items: GalleryItem[],
  config: (typeof GALLERY_VARIANT_CONFIG)[keyof typeof GALLERY_VARIANT_CONFIG],
  sectionId: string
): SectionGalleryPageBlock["images"] {
  return items.map((item) => {
    const src = extractCloudinaryAssetUrl(item.image.image);

    if (!src) {
      throw new Error(
        `Gallery Section "${sectionId}" contains item "${item.id ?? "unknown"}" without a usable image.`
      );
    }

    const responsive = getCloudinarySrcSet(src, {
      transform: resolveTransform(config.transform, item.image.hasFocalPoint),
    });

    return {
      sys: item.sys,
      textEntrySys: item.textEntrySys,
      src: responsive?.src ?? src,
      srcSet: responsive?.srcSet,
      aspectRatio: config.aspectRatio,
      alt: item.image.alternativeText ?? item.internalName ?? "",
      headline: item.headline,
      text: item.text,
    };
  });
}

export function SectionGalleryAdapter(
  item: SectionGalleryInput,
  index: number
): SectionGalleryPageBlock {
  if (!Array.isArray(item.itemsCollection?.items)) {
    throw new TypeError(
      "SectionGalleryAdapter: itemsCollection.items is required"
    );
  }

  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  const sectionId = getGallerySectionId(item);
  const variant = mapGalleryVariant(item.variant);
  const galleryItems = normalizeGalleryItems(
    item.itemsCollection.items,
    sectionId
  );

  return {
    id: getBlockId(item, index),
    __typename: item.__typename,
    sys: item.sys,
    internalName: item.internalName,
    variant,
    headline: item.headline,
    images: mapGalleryItems(
      galleryItems,
      GALLERY_VARIANT_CONFIG[variant],
      sectionId
    ),
    fullWidth: item.fullWidth ?? undefined,
    background: uiConfig?.backgroundColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
